/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { forwardRef } from 'react';

type Type = {
  type?: 'button' | 'reset' | 'submit';
};

type Variants =
  | { ghost: true; primary?: false | never; secondary?: false | never }
  | { ghost?: false | never; primary: true; secondary?: false | never }
  | { secondary: true; ghost?: false | never; primary?: false | never };

type OwnProps = Variants & Type;

export type Props = OwnProps & Omit<React.ComponentPropsWithRef<'button'>, keyof OwnProps>;

const styles = {
  default: css`
    background: transparent;
    border: none;
    border-radius: var(--cds-border-radius-100);
    font-size: 14px;
    font-weight: bold;
    line-height: 24px;
    text-align: center;
    transition: background-color 250ms ease-out, box-shadow 250ms ease-out, color 250ms ease-out;

    &:focus {
      outline: 2px solid var(--cds-color-purple-700);
      outline-offset: 2px;
    }
  `,

  ghost: css`
    color: var(--cds-color-blue-800);
    padding: 12px 16px;

    @media (hover: hover) {
      &:hover {
        background-color: var(--cds-color-blue-25);
        color: var(--cds-color-interactive-primary-hover);
      }
    }

    &:focus {
      background-color: var(--cds-color-blue-25);
      box-shadow: 0 0 0 2px var(--cds-color-white-0), 0 0 0 3px var(--cds-color-purple-700),
        0 0 0 4px var(--cds-color-blue-25);
      color: var(--cds-color-callouts-tertiary);
      text-decoration: underline;
    }

    &:active {
      background-color: var(--cds-color-emphasis-quaternary-background-xweak);
      color: var(--cds-color-emphasis-quaternary-background-xstrong);
    }

    &[disabled] {
      background-color: var(--cds-color-grey-200);
      color: var(--cds-color-grey-25);
    }
  `,

  primary: css`
    background-color: var(--cds-color-blue-800);
    color: var(--cds-color-neutral-primary-invert);
    padding: 12px 32px;

    @media (hover: hover) {
      &:hover {
        background-color: var(--cds-color-interactive-primary-hover);
      }
    }

    &:focus {
      background-color: var(--cds-color-blue-800);
      box-shadow: 0 0 0 2px var(--cds-color-white-0), 0 0 0 3px var(--cds-color-purple-700),
        0 0 0 4px var(--cds-color-blue-25);
    }

    &:active {
      background-color: var(--cds-color-emphasis-quaternary-background-xstrong);
    }

    &[disabled] {
      background-color: var(--cds-color-grey-200);
      color: var(--cds-color-grey-25);
    }
  `,
  secondary: css`
    color: var(--cds-color-blue-800);
    padding: 12px 32px;
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-blue-800);

    @media (hover: hover) {
      &:hover {
        background-color: var(--cds-color-blue-25);
        color: var(--cds-color-interactive-primary-hover);
      }
    }

    &:focus {
      background-color: var(--cds-color-blue-25);
      box-shadow: 0 0 0 2px var(--cds-color-white-0), 0 0 0 3px var(--cds-color-purple-700),
        0 0 0 4px var(--cds-color-blue-25);
      color: var(--cds-color-callouts-tertiary);
    }

    &:active {
      background-color: var(--cds-color-emphasis-quaternary-background-xweak);
      color: var(--cds-color-emphasis-quaternary-background-xstrong);
    }

    &[disabled] {
      background-color: var(--cds-color-grey-200);
      color: var(--cds-color-grey-25);
    }
  `,
};

const Button = forwardRef<HTMLButtonElement, Props>(({ ghost, primary, secondary, type = 'button', ...props }, ref) => (
  <button
    {...props}
    css={[styles.default, ghost && styles.ghost, primary && styles.primary, secondary && styles.secondary]}
    ref={ref}
    type={type}
  />
));

export default Button;
